import { Autocomplete, Box, IconButton, TextField, Tooltip } from '@mui/material';
import { Check } from '@mui/icons-material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-pro';
import { useState, useEffect } from 'react';

export interface MetadataFilterOption {
  value: string;
  count: number;
}

interface MetadataFilterInputProperties extends GridFilterInputValueProps {
  options?: MetadataFilterOption[];
  onApply?: () => void;
}

const mapStringValuesToOptions = (stringValues: string[], options: MetadataFilterOption[]): MetadataFilterOption[] => {
  return stringValues
    .map(value => options.find(opt => opt.value === value) ?? { value, count: 0 })
    .filter((opt): opt is MetadataFilterOption => opt !== undefined);
};

const hasValuesChanged = (localValues: MetadataFilterOption[], appliedValues: MetadataFilterOption[]): boolean => {
  if (localValues.length !== appliedValues.length) return true;
  const localValueSet = new Set(localValues.map(v => v.value));
  return !appliedValues.every(v => localValueSet.has(v.value));
};

const extractOptionValues = (options: MetadataFilterOption[]): string[] => {
  return options.map(option => option.value);
};

export const MetadataFilterInput = ({ item, applyValue, options = [], onApply }: MetadataFilterInputProperties) => {
  const appliedValues: MetadataFilterOption[] = Array.isArray(item.value)
    ? mapStringValuesToOptions(item.value, options)
    : [];
  const [localValues, setLocalValues] = useState<MetadataFilterOption[]>(() => appliedValues);

  useEffect(() => {
    setLocalValues(appliedValues);
  }, [item.value, options]);

  const hasChanges = hasValuesChanged(localValues, appliedValues);

  const handleChange = (_event: React.SyntheticEvent, newValue: MetadataFilterOption[]) => {
    setLocalValues(newValue);
    const wasRemoved = newValue.length < localValues.length;
    if (wasRemoved) {
      applyValue({ ...item, value: extractOptionValues(newValue) });
      onApply?.();
    }
  };

  const handleApply = () => {
    applyValue({ ...item, value: extractOptionValues(localValues) });
    onApply?.();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      <Autocomplete
        multiple
        size='small'
        disableClearable
        options={options}
        value={localValues}
        onChange={handleChange}
        getOptionLabel={option => `${option.value} (${option.count})`}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        slotProps={{
          chip: {
            size: 'small' as const,
          },
        }}
        renderInput={parameters => (
          <TextField
            {...parameters}
            variant='outlined'
            label='Value'
            placeholder={localValues.length === 0 ? 'Select values...' : ''}
          />
        )}
        sx={{ flex: 1, minWidth: 0 }}
      />
      {hasChanges && (
        <Tooltip title='Apply'>
          <IconButton size='small' onClick={handleApply} color='primary' sx={{ mt: 0.5 }}>
            <Check sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
